import Grid from "@mui/material/Grid"
import React, { useEffect, useState } from "react"
import { Box, Button, CircularProgress } from "@mui/material"
import {
  useGetModelQuery,
  useLazyGetModelJsonQuery,
  useLazyGetRunModelQuery,
} from "@store/services/modelsService"
import { Navigate, useParams } from "react-router"
import { ModelAndRunMetadataCard } from "./ModelAndRunMetadataCard"
import DownloadIcon from "@mui/icons-material/Download"
import "./ViewModel.css"
import { useGetGraphQuery, useGetUserGraphQuery } from "@store/services/graphsService"
import { skipToken } from "@reduxjs/toolkit/dist/query/react"
import { GraphContainer } from "@features/graph/GraphContainer"
import RunModelButton from "@features/run/viewModel/RunModelButton"
import RunModelDialog from "@features/run/viewModel/RunModelDialog"
import { RunModelFormValues } from "@features/run/RunModelForm"
import { GraphEditMode } from "@features/graph/graphViewModel"
import { downloadFile } from "@utils/file"
import logger from "@utils/logger"

export default function ViewModel() {
  const { id } = useParams()
  const { data: runModel, isFetching } = useGetModelQuery(id || "")

  const [fetchRunModel, { data: runResults, isFetching: isFetchingRunModel }] =
    useLazyGetRunModelQuery()
  const [isRunModelDialogOpen, setIsRunModelDialogOpen] = useState(false)
  const [runResponse, setRunResponse] = useState(undefined)

  const skipBaseGraph = runModel?.user_graph_key || !runModel?.graph_key
  const skipUserGraph = !runModel?.user_graph_key

  const { data: userGraph, isFetching: isFetchingUserGraph } = useGetUserGraphQuery(
    skipUserGraph ? skipToken : runModel?.user_graph_key,
  )
  const { data: baseGraph, isFetching: isFetchingBaseGraph } = useGetGraphQuery(
    skipBaseGraph ? skipToken : runModel?.graph_key,
  )

  const [fetchModelJson, { data: json, isError: isFetchModelJsonError }] =
    useLazyGetModelJsonQuery()

  useEffect(() => {
    if (isFetchModelJsonError || !json) return

    const fileUrl = "data:text/json;chatset=utf-8," + encodeURIComponent(JSON.stringify(json))
    downloadFile(`${runModel?.key}-multistep.json`, fileUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [json, isFetchModelJsonError])

  const graph = userGraph ?? baseGraph

  const handleRun = async () => {
    setIsRunModelDialogOpen(true)
  }

  const handleExport = async () => {
    if (runModel?.key) {
      fetchModelJson(runModel.key)
    }
  }

  const handleRunModalClick = async (formValues: RunModelFormValues) => {
    if (!runModel) {
      logger.error("handleRunModalClick->empty upload", runModel)
      return
    }
    const resp = await fetchRunModel({
      key: runModel.key,
      seed: formValues.seed,
      numSamples: formValues.number_of_samplings,
      samplingEngine: "EVIEWS",
      runName: formValues.run_name,
      contractKey: formValues.contract_key || undefined,
    })
    setRunResponse(resp.data.run_key)
  }
  // Navigate to run response
  if (runResponse) {
    return <Navigate to={`/Run/Progress/${runResponse}`} />
  }

  if (isFetching || isFetchingUserGraph || isFetchingBaseGraph) {
    return <CircularProgress />
  }

  return (
    <>
      <Grid container sx={{ height: "calc(100vh - 64px)", pl: 0 }} wrap="nowrap" gap={1}>
        <Grid container item direction="column" xs gap={1} wrap="nowrap">
          <Grid item xs>
            <GraphContainer
              graph={graph}
              showAddToSelection={true}
              isUserGraph={!!runModel?.user_graph_key} // a model may be a user graph or user graph!
              showUserGraphIndicator={true}
              showModified={false}
              showExportGraph={true}
              showResetGraph={false}
              graphFetchingErrorMessage="Preview can't be loaded"
              selectionChangeCallback={() => {}}
              editMode={GraphEditMode.OFF}
            />
          </Grid>
          <Grid item direction="row">
            <ModelAndRunMetadataCard
              model={runModel}
              runName={"Model Metadata"}
              seed={0}
              samples={0}
            />
            {isFetchingRunModel ? (
              <Box
                sx={{
                  display: "inline-block",
                  top: "4px",
                  position: "relative",
                }}
              >
                <CircularProgress
                  sx={{
                    top: "10px",
                    position: "relative",
                    marginLeft: "8px",
                    marginRight: "8px",
                  }}
                />
              </Box>
            ) : (
              <>
                <RunModelButton onClick={handleRun} model={runModel} />
                <Button
                  startIcon={<DownloadIcon />}
                  variant="contained"
                  size={"medium"}
                  sx={{ m: 1, background: "black" }}
                  color="error"
                  onClick={handleExport}
                >
                  Export Multistep JSON
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
      <RunModelDialog
        open={isRunModelDialogOpen}
        graph={graph}
        onClose={() => setIsRunModelDialogOpen(false)}
        onSubmit={handleRunModalClick}
      />
    </>
  )
}
